import React, { useState, useMemo, useCallback } from 'react';
import { updateUserActiveStatus, promoteToAdmin, demoteToUser, updateUserProfile } from '../../services/api/user';
import { AppUser } from '../../services/api/user';
import { useHomePageData } from '../../hooks/useHomePageData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { MoreHorizontal, ChevronLeft, ChevronRight, Search, ArrowUpDown, ArrowUp, ArrowDown, Users, Edit, Key, Shield, ShieldCheck, UserCheck, UserX, Loader2 } from 'lucide-react';
import SvgSpinner from '../../components/Spinners/SvgSpinner';
import { toast } from 'sonner';
import { auth } from '../../firebase-config';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';

type SortKey = 'displayName' | 'email' | 'role' | 'organizationId' | 'active' | 'createdAt' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

const UserManagementPage: React.FC = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const { data, loading: dataLoading, error: dataError, refetch } = useHomePageData(authUser?.role || null);
  
  // Extract data from the cached data (memoized to satisfy hook deps)
  const users = useMemo(() => data?.users ?? [], [data?.users]);
  const organizations = useMemo(() => data?.organizations ?? [], [data?.organizations]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: ''
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Debug logging
  console.log('UserManagementPage: Component rendered', { authUser, authLoading });

  // Precompute organization id -> name map once per organizations change
  const orgNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) {
      if (org && org.id) map.set(org.id, org.name);
    }
    return map;
  }, [organizations]);

  // Organization name resolver (stable)
  const getOrganizationName = useCallback(
    (organizationId: string | null | undefined) => {
      if (!organizationId) return 'No Organization';
      return orgNameMap.get(organizationId) || 'No Organization';
    },
    [orgNameMap]
  );

  // Handle sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Filter and paginate users
  const filteredUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = 
        user.displayName?.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        user.uid.toLowerCase().includes(search.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' ? user.active : !user.active);
      
      return matchesSearch && matchesRole && matchesStatus;
    });
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortKey) {
        case 'displayName':
          aValue = a.displayName || '';
          bValue = b.displayName || '';
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'organizationId':
          aValue = getOrganizationName(a.organizationId).toLowerCase();
          bValue = getOrganizationName(b.organizationId).toLowerCase();
          break;
        case 'active':
          aValue = a.active;
          bValue = b.active;
          break;
        case 'createdAt':
          aValue = a.createdAt?._seconds || 0;
          bValue = b.createdAt?._seconds || 0;
          break;
        case 'lastLogin':
          aValue = a.lastLogin?._seconds || 0;
          bValue = b.lastLogin?._seconds || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [users, search, roleFilter, statusFilter, sortKey, sortDirection, getOrganizationName]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleUpdateActiveStatus = async (uid: string, active: boolean) => {
    try {
      await updateUserActiveStatus(uid, active);
      await refetch(); // Reload data from cache
      toast.success(`User ${active ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      toast.error('Failed to update user active status');
      console.error(err);
    }
  };

  const handlePromoteToAdmin = async (uid: string) => {
    try {
      await updateUserActiveStatus(uid, true);
      await promoteToAdmin(uid);
      await refetch(); // Reload data from cache
      toast.success('User promoted to admin successfully!');
    } catch (err) {
      toast.error('Failed to promote user to admin');
      console.error(err);
    }
  };

  const handleDemoteToUser = async (uid: string) => {
    try {
      await demoteToUser(uid);
      await refetch(); // Reload data from cache
      toast.success('User demoted to regular user successfully!');
    } catch (err) {
      toast.error('Failed to demote user');
      console.error(err);
    }
  };

  const handleEditProfile = (user: AppUser) => {
    setSelectedUser(user);
    setProfileForm({
      displayName: user.displayName || '',
      email: user.email || ''
    });
    setIsProfileDialogOpen(true);
  };

  const handleProfileUpdate = async () => {
    if (!selectedUser) return;
    
    try {
      setIsUpdatingProfile(true);
      await updateUserProfile(selectedUser.uid, profileForm.displayName);
      await refetch(); // Reload data from cache
      setIsProfileDialogOpen(false);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error('Failed to update profile');
      console.error(err);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
    } catch (err) {
      toast.error('Failed to send password reset email');
      console.error(err);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Never';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp format {_seconds, _nanoseconds}
      if (timestamp._seconds && typeof timestamp._seconds === 'number') {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle Firestore Timestamp object
      else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle regular Date object or timestamp number
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        console.warn('Unknown timestamp format:', timestamp);
        return 'Invalid date 1';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid date';
    }
  };

  // getOrganizationName defined above via useCallback

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <SvgSpinner />
          <p className="mt-4">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="text-center text-red-600">
        <p>Non sei loggato. Per favore effettua il log in.</p>
      </div>
    );
  }

  if (authUser.role !== 'admin') {
    return (
      <div className="text-center text-red-600">
        <p>Access denied. You need admin privileges to view this page.</p>
        <p>Current role: {authUser.role}</p>
      </div>
    );
  }

  if (dataLoading || authLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="text-center text-red-600">
        <p>{dataError}</p>
        <Button onClick={refetch} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg">
            <Users className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">User Management</h1>
        </div>
        <p className="text-indigo-400 font-medium ml-14">Manage all users in the system.</p>
      </div>


      {/* Users Table */}
      <Card className="bg-gradient-to-r from-white to-gray-50 border-gray-200">
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <CardDescription className="text-sm text-muted-foreground">
              Show {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
            </CardDescription>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search name, email or UID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-200"
                  />
                </div>
              </div>
              <div className="w-full sm:w-32">
                <Select value={roleFilter} onValueChange={(value: 'all' | 'admin' | 'user') => setRoleFilter(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-32">
                <Select value={statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive') => setStatusFilter(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-indigo-100 to-purple-100 border-indigo-200">
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('displayName')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Username {getSortIcon('displayName')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('email')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Contact {getSortIcon('email')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('role')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Role {getSortIcon('role')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('organizationId')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Organization {getSortIcon('organizationId')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('active')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Status {getSortIcon('active')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('createdAt')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Created Date {getSortIcon('createdAt')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('lastLogin')}
                    className="h-auto p-0 font-semibold hover:bg-indigo-200 text-indigo-900"
                  >
                    Last Login {getSortIcon('lastLogin')}
                  </Button>
                </TableHead>
                <TableHead className="text-indigo-900 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.uid} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{user.displayName || user.email}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>{getOrganizationName(user.organizationId)}</TableCell>
                  <TableCell>
                    <Badge variant={user.active ? 'secondary' : 'destructive'} className={user.active ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' : ''}>
                      {user.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => handleEditProfile(user)} className="text-indigo-700 hover:bg-indigo-50">
                          <Edit className="h-4 w-4 mr-2 text-indigo-600" />
                          Edit Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handlePasswordReset(user.email)} className="text-indigo-700 hover:bg-indigo-50">
                          <Key className="h-4 w-4 mr-2 text-indigo-600" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.role === 'user' ? (
                          <DropdownMenuItem onClick={() => handlePromoteToAdmin(user.uid)} className="text-emerald-700 hover:bg-emerald-50">
                            <Shield className="h-4 w-4 mr-2 text-emerald-600" />
                            Promote to Admin
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleDemoteToUser(user.uid)} className="text-amber-700 hover:bg-amber-50">
                            <ShieldCheck className="h-4 w-4 mr-2 text-amber-600" />
                            Demote to User
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleUpdateActiveStatus(user.uid, !user.active)}
                          className={user.active ? "text-red-700 hover:bg-red-50" : "text-emerald-700 hover:bg-emerald-50"}
                        >
                          {user.active ? (
                            <>
                              <UserX className="h-4 w-4 mr-2 text-red-600" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-2 text-emerald-600" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Precedente
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Successivo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Edit Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Edit User Profile
              {isUpdatingProfile && (
                <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
              )}
            </DialogTitle>
            <DialogDescription>
              Update the user's display name. Email cannot be changed directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Current Display Name Display */}
            <div className="p-4 bg-muted/50 rounded-lg border">
              <Label className="text-sm font-medium text-muted-foreground">Current Display Name</Label>
              <p className="text-lg font-semibold mt-1">{selectedUser?.displayName || 'Not set'}</p>
            </div>
            
            {/* New Display Name Input */}
            <div>
              <Label htmlFor="displayName">New Display Name</Label>
              <Input
                id="displayName"
                placeholder="Enter new display name..."
                value={profileForm.displayName}
                onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                disabled={isUpdatingProfile}
                className="mt-2"
              />
            </div>
            
            {/* Email Display (Read-only) */}
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                value={profileForm.email}
                disabled
                className="bg-muted mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Email cannot be changed directly. Use Firebase Auth to update email.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsProfileDialogOpen(false)}
              disabled={isUpdatingProfile}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleProfileUpdate}
              disabled={isUpdatingProfile}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isUpdatingProfile ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Profile'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPage; 